import {
  createObstacleNodes,
  obstacles,
  pasi,
  pasiFlipped,
  pasiFlash,
  scorePopups,
  SCORE_POPUP_DURATION,
} from './mechanics'

export const SCALE = 1.6

export const SURFACE_WIDTH = 240 * SCALE
const SURFACE_HEIGHT = 40 * SCALE
export const TREES_WIDTH = 240 * SCALE
const TREES_HEIGHT = 60 * SCALE
export const PASI_WIDTH = 120 * SCALE
export const PASI_HEIGHT = 40 * SCALE

export let width = 0
let height = 0
export let ground = 0

const FLASH_DURATION = 300 // ms
let flashOpacity = 0

const PASI_IMG = `/assets/pasi.png`
const CRATES_IMG = `/assets/crates.png`
const SPIKES_IMG = `/assets/spikes.png`
const TNT_IMG = `/assets/tnt.png`
const SURFACE_IMG = `/assets/surface.png`
const TREES_IMG = `/assets/trees.png`

const pasiImg = new Image()
pasiImg.src = PASI_IMG
const cratesImg = new Image()
cratesImg.src = CRATES_IMG
const spikesImg = new Image()
spikesImg.src = SPIKES_IMG
const tntImg = new Image()
tntImg.src = TNT_IMG
const surfaceImg = new Image()
surfaceImg.src = SURFACE_IMG
const treesImg = new Image()
treesImg.src = TREES_IMG

const loadImage = (img: HTMLImageElement): Promise<void> =>
  new Promise((resolve, reject) => {
    const onResolve = () => {
      if ('decode' in img) {
        // decode may reject on unsupported browsers, so ignore failures
        ;(img as HTMLImageElement & { decode: () => Promise<void> })
          .decode()
          .then(() => resolve())
          .catch(() => resolve())
      } else {
        resolve()
      }
    }
    if (img.complete && img.naturalWidth !== 0) {
      onResolve()
    } else {
      img.addEventListener('load', onResolve, { once: true })
      img.addEventListener('error', () => reject(new Error('Failed to load image')), {
        once: true,
      })
    }
  })

export const loadSprites = (): Promise<void> =>
  Promise.all([pasiImg, cratesImg, spikesImg, tntImg, surfaceImg, treesImg].map(loadImage)).then(
    () => undefined
  )

export const getSprites = () => ({
  pasiImg,
  cratesImg,
  spikesImg,
  tntImg,
  surfaceImg,
  treesImg,
})

const drawTiled = (
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  img: HTMLImageElement,
  iw: number,
  ih: number,
  y: number,
  offset: number
) => {
  const ctx = canvasRef.current?.getContext('2d')
  if (!ctx) return
  const start = -iw + (offset % iw)
  for (let x = start; x < width; x += iw) {
    ctx.drawImage(img, x, y, iw, ih)
  }
}

export const draw = (
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  treesOffset: number,
  groundOffset: number
) => {
  const ctx = canvasRef.current?.getContext('2d')
  if (!ctx) return
  ctx.fillStyle = '#87CEEB'
  ctx.fillRect(0, 0, width, height)

  const { treesImg, surfaceImg, pasiImg } = getSprites()

  drawTiled(canvasRef, treesImg, TREES_WIDTH, TREES_HEIGHT, ground - TREES_HEIGHT, treesOffset)
  drawTiled(canvasRef, surfaceImg, SURFACE_WIDTH, SURFACE_HEIGHT, ground, groundOffset)

  obstacles.forEach((o) => {
    const sprite = createObstacleNodes()[o.type]
    ctx.drawImage(sprite.img, o.x, o.y, sprite.width, sprite.height)
  })

  ctx.save()
  if (pasiFlipped) {
    ctx.translate(pasi.x + PASI_WIDTH / 2, pasi.y + PASI_HEIGHT / 2)
    ctx.rotate(Math.PI)
    ctx.drawImage(pasiImg, -PASI_WIDTH / 2, -PASI_HEIGHT / 2, PASI_WIDTH, PASI_HEIGHT)
  } else {
    ctx.drawImage(pasiImg, pasi.x, pasi.y, PASI_WIDTH, PASI_HEIGHT)
  }
  ctx.restore()

  scorePopups.forEach((p) => {
    ctx.save()
    ctx.globalAlpha = 1 - p.age / SCORE_POPUP_DURATION
    ctx.fillStyle = 'white'
    ctx.font = `${40 * SCALE}px sans-serif`
    ctx.textAlign = 'center'
    ctx.fillText('+1', p.x, p.y)
    ctx.restore()
  })

  if (pasiFlash) {
    flashOpacity = 1
  }
  if (flashOpacity > 0) {
    ctx.fillStyle = `rgba(255, 0, 0, ${flashOpacity * 0.5})`
    ctx.fillRect(0, 0, width, height)
    flashOpacity -= (1 / FLASH_DURATION) * 16.66 // Assuming 60fps, 16.66ms per frame
  }
}

export const resize = (canvas: HTMLCanvasElement) => {
  const dpr = window.devicePixelRatio || 1
  canvas.width = document.documentElement.clientWidth * dpr
  canvas.height = document.documentElement.clientHeight * dpr
  canvas.style.width = `${document.documentElement.clientWidth}px`
  canvas.style.height = `${document.documentElement.clientHeight}px`
  width = canvas.width
  height = canvas.height
  ground = height - SURFACE_HEIGHT
  pasi.y = ground - PASI_HEIGHT
}
